import React from "react";

/**
 * ChartNarrativeDifferential displays an executive summary for the differential chart.
 *
 * It assumes:
 * - The cheapest mix (baseline for cost) is provided as `bestCost`
 * - The costliest mix is provided as `worstCost`
 * - The greenest mix (baseline for emissions) is provided as `bestEmission`
 * - The worst emission mix is provided as `worstEmission`
 *
 * Each mix object must have:
 *   - dollarTotal (number): the cost in '000 USD
 *   - iTotal (number): the CO₂ emission in '000 t CO₂‑eq
 */
function ChartNarrativeDifferential({ bestCost, worstCost, bestEmission, worstEmission }) {
  // Calculate differential cost values (relative to the cheapest mix as baseline).
  const costDeltaLowEmission = bestEmission.dollarTotal - bestCost.dollarTotal;
  const costDeltaPriciest    = worstCost.dollarTotal - bestCost.dollarTotal;
  const costDeltaWorstEmission = worstEmission.dollarTotal - bestCost.dollarTotal;

  // Calculate differential emission values (relative to the lowest-emission mix as baseline).
  const emissionDeltaCheapest  = bestCost.iTotal - bestEmission.iTotal;
  const emissionDeltaHighCost  = worstCost.iTotal - bestEmission.iTotal;
  const emissionDeltaWorst     = worstEmission.iTotal - bestEmission.iTotal;

  return (
    <section className="prose mt-6 max-w-none">
      <h3>Executive insight — Differential Impact Analysis</h3>

      <p>
        Using the cheapest mix as the <em>cost baseline</em> and the lowest-emission mix as the <em>carbon baseline</em>, we find:
      </p>

      <ul>
        <li>
          Upgrading from the cheapest option to the low‑emission mix costs an extra{" "}
          <strong>${costDeltaLowEmission.toLocaleString()} k</strong>.
        </li>
        <li>
          The costliest mix demands an additional{" "}
          <strong>${costDeltaPriciest.toLocaleString()} k</strong> over the cheapest alternative.
        </li>
        <li>
          The mix with the worst emissions costs an extra{" "}
          <strong>${costDeltaWorstEmission.toLocaleString()} k</strong> relative to the baseline.
        </li>
      </ul>

      <p>
        In emissions terms (relative to the green baseline):
      </p>

      <ul>
        <li>
          The cheapest mix emits an extra <strong>{emissionDeltaCheapest} t CO₂‑eq</strong>.
        </li>
        <li>
          The costliest option adds an extra <strong>{emissionDeltaHighCost} t CO₂‑eq</strong>.
        </li>
        <li>
          The worst-emission mix increases emissions by an additional <strong>{emissionDeltaWorst} t CO₂‑eq</strong>.
        </li>
      </ul>

      <p>
        <strong>Bottom line:</strong> A modest cost premium for the low‑emission option secures substantial carbon savings, while the steep extra cost of the costliest alternative is not justified by its environmental performance.
      </p>
    </section>
  );
}

export default ChartNarrativeDifferential;
